import React from 'react';
import { LLink as Link } from '../components/LocalizedLink';
import { ArrowRight, Award, Users, Globe2, Heart, Check } from 'lucide-react';
import Seo from '../components/Seo';
import { useI18n } from '../context/I18nContext';
import UI from '../lib/uiCatalog';

const TEAM = [
  {
    name: 'Björn Ingbrant',
    roleKey: 'about.role.bjorn',
    img: 'https://enovaestates.com/wp-content/uploads/2022/09/Bjorn-Ingbrant-Rubio.jpg',
  },
  {
    name: 'Tanja Rubio',
    roleKey: 'about.role.tanja',
    img: 'https://enovaestates.com/wp-content/uploads/2022/09/Tanja-Ingbrant-Rubio.jpg',
  },
  {
    name: 'Amor Alcazar',
    roleKey: 'about.role.amor',
    img: 'https://enovaestates.com/wp-content/uploads/2022/09/Amor-Alcazar.jpg',
  },
  {
    name: 'Sofia Sofocli',
    roleKey: 'about.role.sofia',
    img: 'https://easyspanishtax.com/api/cms-assets/a9d9f216-19d8-469f-a16e-8fbcb0024c57.jpg',
  },
];

const OFFICE_IMG =
  'https://customer-assets.emergentagent.com/job_modelo210-rebuild/artifacts/9go5sel8_the-office.jpg';

export default function About() {
  const { t } = useI18n();
  const T = (k) => t(k, UI[k]);

  const values = [
    { icon: Award,   title: T('about.value_independent_title'), text: T('about.value_independent_text') },
    { icon: Users,   title: T('about.value_expats_title'),      text: T('about.value_expats_text') },
    { icon: Globe2,  title: T('about.value_multi_title'),       text: T('about.value_multi_text') },
    { icon: Heart,   title: T('about.value_transp_title'),      text: T('about.value_transp_text') },
  ];

  const features = [
    'about.feature_designed',
    'about.feature_save',
    'about.feature_clear',
    'about.feature_pay',
  ];

  return (
    <div>
      <Seo pageKey="about" />

      {/* Hero */}
      <section className="hero-bg">
        <div className="container-x pt-16 pb-14 text-center max-w-3xl mx-auto">
          <span className="eyebrow" data-testid="about-eyebrow">{T('about.eyebrow')}</span>
          <h1
            data-testid="about-title"
            className="mt-4 text-4xl md:text-5xl font-bold text-[#0b1f3a] tracking-tight"
          >
            {T('about.title')}
          </h1>
        </div>
      </section>

      {/* Enova story */}
      <section className="pb-6">
        <div className="container-x">
          <figure className="max-w-4xl mx-auto">
            <img
              src={OFFICE_IMG}
              alt="Enova Estates office in La Duquesa, Manilva"
              className="w-full h-[260px] sm:h-[360px] object-cover rounded-3xl shadow-lg"
              onError={(e) => {
                e.currentTarget.src =
                  'https://images.unsplash.com/photo-1512917774080-9991f1c4c750';
              }}
              data-testid="about-office-img"
            />
            <figcaption className="mt-3 text-center text-sm text-slate-500 italic">
              {T('about.enova_caption')}
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="pt-6 pb-16">
        <div className="container-x max-w-3xl mx-auto space-y-5 text-slate-700 leading-relaxed">
          <p data-testid="about-subtitle">{T('about.subtitle')}</p>
          <p>{T('about.enova_p1')}</p>
          <p>{T('about.enova_p2')}</p>
          <p>{T('about.enova_p3')}</p>

          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pt-4">
            {features.map((k) => (
              <li key={k} className="flex items-start gap-2">
                <span className="mt-0.5 text-emerald-600 shrink-0"><Check size={18} /></span>
                <span className="text-[#0b1f3a] font-medium">{T(k)}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Value tiles */}
      <section className="pb-20">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-6">
          {values.map((v) => (
            <div
              key={v.title}
              className="bg-white border border-slate-200 rounded-2xl p-7 card-lift"
              data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <span className="icon-tile mb-4"><v.icon size={20} /></span>
              <h3 className="text-xl font-semibold text-[#0b1f3a]">{v.title}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="py-20 bg-[#f7fafd] border-y border-slate-200">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto">
            <span className="eyebrow" data-testid="about-team-eyebrow">{T('about.team_eyebrow')}</span>
            <h2
              data-testid="about-team-title"
              className="section-title mt-3"
            >
              {T('about.team_title')}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">{T('about.team_subtitle')}</p>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {TEAM.map((m) => (
              <div
                key={m.name}
                className="bg-white border border-slate-200 rounded-2xl p-5 text-center card-lift"
                data-testid={`about-team-${m.name.split(' ')[0].toLowerCase()}`}
              >
                <div className="w-28 h-28 sm:w-32 sm:h-32 mx-auto rounded-full overflow-hidden ring-4 ring-white shadow-md">
                  <img
                    src={m.img}
                    alt={m.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(m.name)}`;
                    }}
                  />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-[#0b1f3a]">{m.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{T(m.roleKey)}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/home-insurance" className="btn-primary" data-testid="about-cta-quote">
              {T('cta.get_quote')} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
